import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select as MuiSelect,
} from '@mui/material';
import { Calendar, Phone, Mail, CheckCircle, Users, Sparkles } from 'lucide-react';
import { KeyUri, config } from "../key";
import axios from 'axios';
import { jwtDecode } from "jwt-decode";

export function BookingSection() {
  // Form fields state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    wellnessProgram: '',
    preferredDate: '',
    guests: '',
  });

  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [wellnessPackages, setWellnessPackages] = useState([]);
  const [userId, setUserId] = useState(null);

  // Get user ID from token if logged in
  useEffect(() => {
    const tokenUser = localStorage.getItem("tokenUser") || localStorage.getItem("tokenuser");
    if (tokenUser) {
      try {
        const token = tokenUser.replace(/^"|"$/g, "");
        const decoded = jwtDecode(token);
        setUserId(decoded.id || decoded._id || decoded.userId);
      } catch (err) {
       // console.log("User not logged in or invalid token");
      }
    }
  }, []);

  // Fetch wellness packages from API
  useEffect(() => {
    const fetchWellnessPackages = async () => {
      try {
        const response = await axios.get(`${KeyUri.BACKEND_URI}/wellness-packages`);
        // Filter only available packages and sort by order
        const availablePackages = response.data
          .filter(pkg => pkg.available)
          .sort((a, b) => a.order - b.order);
        setWellnessPackages(availablePackages);
      } catch (err) {
        console.error('Failed to fetch wellness packages:', err);
      }
    };
    fetchWellnessPackages();
  }, []);

  useEffect(() => {
    let timer;
    if (submissionStatus === 'success' || submissionStatus === 'error') {
      timer = setTimeout(() => {
        setSubmissionStatus(null);
      }, 4000);
    }
    return () => clearTimeout(timer);
  }, [submissionStatus]);
useEffect(() => {
  let mounted = true;
  let pollInterval;
  let bookingObserver;
  let bookingSectionRef;

  const checkProgramData = () => {
    const section = document.getElementById('booking');
    if (!section?.dataset.selectedProgram || !mounted) return;

    try {
      const program = JSON.parse(section.dataset.selectedProgram);
    

      setSelectedProgram(program);

      // Try to find the matching package ID from the fetched packages
      const matchingPackage = wellnessPackages.find(
        pkg => pkg.title === program.title || pkg._id === program._id
      );

      if (matchingPackage) {
        setFormData(prev => ({
          ...prev,
          wellnessProgram: matchingPackage._id
        }));
       // console.log('SET DROPDOWN:', matchingPackage._id);
      }

      delete section.dataset.selectedProgram;
    } catch (e) {
      console.error('PARSE ERROR:', e);
    }
  };

  // 1. Polling
  pollInterval = setInterval(checkProgramData, 250);

  // 2. Scroll trigger
  bookingObserver = new IntersectionObserver(
    (entries) => entries.forEach(entry => entry.isIntersecting && checkProgramData()),
    { threshold: 0.1 }
  );

  // 3. Get section ONCE
  bookingSectionRef = document.getElementById('booking');
  if (bookingSectionRef) {
    bookingObserver.observe(bookingSectionRef);
  }

  // 4. Multiple checks
  [100, 400, 800, 1200].forEach(delay => setTimeout(checkProgramData, delay));

  return () => {
    mounted = false;
    if (pollInterval) clearInterval(pollInterval);
    if (bookingObserver && bookingSectionRef) {
      bookingObserver.unobserve(bookingSectionRef);
    }
  };
}, [wellnessPackages]);


  // Handle input changes
  const handleChange = (e) => {
    const { id, name, value } = e.target;
    setFormData((prev) => ({ ...prev, [id || name]: value }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmissionStatus(null);

    try {
      // Include userId if user is logged in
      const bookingData = {
        ...formData,
        ...(userId && { userId })
      };

      const response = await fetch(KeyUri.BACKEND_URI + '/wellness-bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData),
      });

      if (response.ok) {
        setSubmissionStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          wellnessProgram: '',
          preferredDate: '',
          guests: '',
        });
      } else {
        setSubmissionStatus('error');
      }
    } catch (err) {
      setSubmissionStatus('error');
    }
    setLoading(false);
  };

  return (
    <section className="py-24 bg-gradient-to-br from-emerald-900 via-teal-900 to-green-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-400 rounded-full blur-3xl"></div>
      </div>

      {/* FIXED: Use Tailwind container instead of MUI Box */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
            className="inline-block px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-6"
          >
            <Typography variant="subtitle1" sx={{ color: 'rgba(220, 252, 231, 0.9)' }}>
              Begin Your Transformation
            </Typography>
          </motion.div>
          <Typography variant="h4" color="common.white" mb={2} sx={{ fontWeight: 600 }}>
            Reserve Your Wellness Journey
          </Typography>
          <Typography variant="body1" color="rgba(220,252,231,0.8)" sx={{ maxWidth: 560, mx: 'auto' }}>
            Take the first step towards profound healing. Our wellness advisors will help you choose the perfect program for your unique needs.
          </Typography>
        </motion.div>
 {selectedProgram && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white/20 backdrop-blur-xl border border-white/30 rounded-2xl p-6 mb-8 max-w-md mx-auto"
          >
            <div className="flex items-start gap-4">
              <img 
                src={selectedProgram.image} 
                alt={selectedProgram.title}
                className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-white mb-1 truncate">{selectedProgram.title}</h3>
                <p className="text-emerald-100 text-sm font-medium mb-2">{selectedProgram.duration}</p>
                <p className="text-emerald-200/90 text-xs leading-relaxed line-clamp-3">
                  {selectedProgram.description}
                </p>
                <div className="mt-3 pt-3 border-t border-white/20">
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-200 rounded-full text-xs font-medium">
                    Pre-selected for you
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* FIXED: Use Tailwind grid instead of MUI Box grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Booking Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl"
          >
            <form noValidate onSubmit={handleSubmit}>
              {/* Name fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <TextField
                  id="firstName"
                  label="First Name"
                  variant="filled"
                  value={formData.firstName}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'rgba(255,255,255,0.8)' } }}
                  InputProps={{ 
                    style: { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                    disableUnderline: true
                  }}
                  placeholder="Enter your first name"
                  fullWidth
                  required
                />
                <TextField
                  id="lastName"
                  label="Last Name"
                  variant="filled"
                  value={formData.lastName}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'rgba(255,255,255,0.8)' } }}
                  InputProps={{ 
                    style: { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                    disableUnderline: true
                  }}
                  placeholder="Enter your last name"
                  fullWidth
                  required
                />
              </div>

              {/* Email */}
              <div className="mb-4">
                <TextField
                  id="email"
                  label="Email Address"
                  type="email"
                  variant="filled"
                  value={formData.email}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'rgba(255,255,255,0.8)' } }}
                  InputProps={{
                    style: { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                    disableUnderline: true,
                    startAdornment: <Mail size={20} style={{ marginRight: 8, marginTop: 16, color: 'rgba(255,255,255,0.6)' }} />,
                  }}
                  placeholder="your@email.com"
                  fullWidth
                  required
                />
              </div>

              {/* Phone */}
              <div className="mb-4">
                <TextField
                  id="phone"
                  label="Phone Number"
                  type="tel"
                  variant="filled"
                  value={formData.phone}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'rgba(255,255,255,0.8)' } }}
                  InputProps={{
                    style: { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                    disableUnderline: true,
                    startAdornment: <Phone size={20} style={{ marginRight: 8, marginTop: 16, color: 'rgba(255,255,255,0.6)' }} />,
                  }}
                  placeholder="+91 0000000000"
                  fullWidth
                  required
                />
              </div>

              {/* Wellness Program */}
              <div className="mb-4">
                <FormControl fullWidth variant="filled">
                  <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>Wellness Program</InputLabel>
                  <MuiSelect
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      '& .MuiSelect-select': { paddingLeft: 2 },
                      '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.8)' },
                      '&:before, &:after': { display: 'none' },
                    }}
                    id="wellnessProgram"
                    name="wellnessProgram"
                    value={formData.wellnessProgram}
                    onChange={handleChange}
                    label="Wellness Program"
                    required
                  >
                    {wellnessPackages.length > 0 ? (
                      wellnessPackages.map((pkg) => (
                        <MenuItem key={pkg._id} value={pkg._id}>
                          {pkg.title} - {pkg.duration}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>Loading programs...</MenuItem>
                    )}
                  </MuiSelect>
                </FormControl>
              </div>

              {/* Date and Guests */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <TextField
                  id="preferredDate"
                  label="Preferred Date"
                  type="date"
                  variant="filled"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true, style: { color: 'rgba(255,255,255,0.8)' } }}
                  InputProps={{
                    style: { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                    disableUnderline: true,
                    startAdornment: <Calendar size={20} style={{ marginRight: 8, marginTop: 16, color: 'rgba(255,255,255,0.6)' }} />,
                  }}
                  fullWidth
                  required
                />
                <FormControl fullWidth variant="filled">
                  <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>Guests</InputLabel>
                  <MuiSelect
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      '& .MuiSelect-select': { paddingLeft: 2 },
                      '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.8)' },
                      '&:before, &:after': { display: 'none' },
                    }}
                    id="guests"
                    name="guests"
                    value={formData.guests}
                    onChange={handleChange}
                    label="Guests"
                    required
                  >
                    <MenuItem value="1">1 Guest</MenuItem>
                    <MenuItem value="2">2 Guests</MenuItem>
                    <MenuItem value="3">3 Guests</MenuItem>
                    <MenuItem value="4">4+ Guests</MenuItem>
                  </MuiSelect>
                </FormControl>
              </div>

              {/* Submit Button */}
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    py: 2.5,
                    mt: 2,
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    background: 'linear-gradient(to right, #10b981, #14b8a6)',
                    '&:hover': {
                      background: 'linear-gradient(to right, #059669, #0d9488)',
                    },
                    '&:disabled': {
                      background: 'rgba(255,255,255,0.3)',
                    },
                  }}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Request Consultation'}
                </Button>
              </motion.div>

              {/* Status Messages */}
              {submissionStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-emerald-500/20 border border-emerald-400/50 rounded-lg"
                >
                  <Typography sx={{ color: '#10b981', textAlign: 'center', fontWeight: 500 }}>
                    ✓ Booking submitted successfully! We will contact you soon.
                  </Typography>
                </motion.div>
              )}

              {submissionStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-red-500/20 border border-red-400/50 rounded-lg"
                >
                  <Typography sx={{ color: '#ef4444', textAlign: 'center', fontWeight: 500 }}>
                    ✗ Failed to submit booking. Please try again later.
                  </Typography>
                </motion.div>
              )}
            </form>
          </motion.div>

          {/* Why Book With Us - Right Column */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">Personalized Consultation</h3>
                  <p className="text-emerald-100/80 text-sm leading-relaxed">
                    Expert wellness advisors will help design your perfect healing journey based on your unique needs and goals.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-teal-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">Small Group Experience</h3>
                  <p className="text-emerald-100/80 text-sm leading-relaxed">
                    Intimate retreats with maximum 12 guests ensure personalized attention and deep transformation.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-green-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">All-Inclusive Packages</h3>
                  <p className="text-emerald-100/80 text-sm leading-relaxed">
                    Accommodation, meals, activities, and therapies included. No hidden costs—just pure focus on your healing.
                  </p>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
              <div className="text-center">
                <div className="flex justify-center gap-8 mb-4">
                  <div>
                    <div className="text-3xl font-bold text-white">500+</div>
                    <div className="text-emerald-200/80 text-sm">Guests Transformed</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">4.9/5</div>
                    <div className="text-emerald-200/80 text-sm">Average Rating</div>
                  </div>
                </div>
                <p className="text-emerald-100/70 text-xs">
                  Join hundreds who have found deep healing in our mountain sanctuaries
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
